import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import "../../styles/AddressForm.css";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import CustomButton from "../../component/ui/FormFields/Button";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ControlledRadioButtonsGroup from "../../component/ui/FormFields/ControlledRadioButtonsGroup";
import {
  addFamilyMemberRequest,
  resetFamilyMember
} from "../../redux/FamilyMembers/actions";
import { selectCountryRequest } from "../../redux/Common/selectorAction";

export default function BookingAddFamilyMember({
  customerNo,
  addFamilyMemberModalClose
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors }
  } = useForm();
  const formValue = watch();
  const customerId = customerNo;

  const countries = useSelector((state) => state.selector?.country?.data);
  const loading = useSelector((state) => state.familyMember.loading);
  const error = useSelector((state) => state.familyMember.error);
  const success = useSelector((state) => state.familyMember.success);

  const countryForCode = countries?.map((value) => {
    return {
      label: ` +${value.phonecode} - ${value.short_name}`,
      value: value.phonecode
    };
  });

  const passengerDetails = [
    {
      name: "first_name",
      label: "First Name *",
      placeholder: "Enter first name",
      rules: { required: "First Name is required" },
      xs: 12,
      sm: 6
    },
    {
      name: "last_name",
      label: "Last Name *",
      placeholder: "Enter last name",
      rules: { required: "Last Name is required" },
      xs: 12,
      sm: 6
    },
    {
      name: "dob",
      label: "Date of Birth *",
      placeholder: "Select date of birth",
      type: "dob",
      rules: { required: "Date of Birth is required" },
      xs: 12,
      sm: 6
    },
    {
      name: "passport_number",
      label: "Passport Number *",
      placeholder: "Enter Passport Number",
      rules: {
        pattern: {
          value: /^[A-Z][0-9]{6,8}$/,
          message: "Passport Number should be in the format A1234567"
        }
      },
      xs: 12,
      sm: 6
    },
    {
      name: "aadhar_number",
      label: "Aadhar Number",
      placeholder: "Enter Aadhar Number",
      type: "Number",
      rules: {
        pattern: {
          value: /^\d{12}$/,
          message: "Aadhar Number must be exactly 12 digits"
        }
      },
      xs: 12,
      sm: 6
    },
    {
      name: "nationality",
      label: "Nationality *",
      placeholder: "Enter Nationality",
      type: "text",
      rules: {
        required: "Nationality is required",
        pattern: {
          value: /^[A-Za-z\s]+$/,
          message: "Only alphabets are allowed"
        }
      },
      xs: 12,
      sm: 6,
      inputProps: {
        onInput: (e) => {
          e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
        }
      }
    },
    {
      name: "country_code",
      label: "Country Code *",
      placeholder: "Enter Country Code",
      options: countryForCode,
      rules: { required: "Country Code is required" },
      xs: 4,
      sm: 2
    },
    {
      name: "phone",
      label: "Mobile No *",
      placeholder: "Enter mobile number",
      type: "Number",
      rules: {
        required: "Mobile No is required",
        pattern: {
          value: /^\d+$/,
          message: "Enter only numeric digits"
        }
      },
      xs: 8,
      sm: 4
    },
    // {
    //   name: 'type',
    //   label: 'Person Type *',
    //   type: 'select',
    //   options: [
    //     { value: '1', label: 'Infant' },
    //     { value: '2', label: 'Child' },
    //     { value: '3', label: 'Adult' },
    //   ],
    //   rules: { required: 'Select a type' },
    //   xs: 12,
    //   sm: 6,
    // },
    {
      name: "gender_type",
      label: "Gender *",
      type: "radio",
      options: [
        { value: "M", label: "Male" },
        { value: "F", label: "Female" },
        { value: "O", label: "Other" }
      ],
      rules: { required: "Select Gender" },
      xs: 12,
      sm: 6
    }
  ];
  useEffect(() => {
    dispatch(selectCountryRequest());
    dispatch(resetFamilyMember());
  }, []);

  const onSubmit = (data) => {
    if (formValue?.aadhar_number === "" && formValue?.passport_number === "") {
      setError("aadhar_number", {
        type: "server",
        message: "Aadhar or Passport Number is needed"
      });
      setError("passport_number", {
        type: "server",
        message: "Aadhar or Passport Number is needed"
      });
      return;
    }
    const formData = {
      ...data,
      country_code: data?.country_code.value,
      customer_id: customerId,
      type: "1"
    };
    dispatch(addFamilyMemberRequest(formData));
  };
  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-center",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: false
      });
    }
  }, [error]);
  useEffect(() => {
    if (success) {
      toast.success("Family member added successfully", {
        position: "top-center",
        autoClose: 3000,
        closeOnClick: true,
        onClose: () => {
          dispatch(resetFamilyMember());
          addFamilyMemberModalClose();
        },
        pauseOnHover: false
      });
    }
  }, [success, dispatch, addFamilyMemberModalClose]);
  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card className="form-card">
          <CardContent>
            <Typography
              variant="h5"
              component="h1"
              gutterBottom
              className="form-heading"
            >
              Add Family Member
            </Typography>
            <Divider className="form-divider" />
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {passengerDetails.map((field, index) => (
                  <Grid item xs={field.xs} sm={field.sm} key={index}>
                    {field.type === "radio" ? (
                      <ControlledRadioButtonsGroup
                        name={field.name}
                        control={control}
                        label={field.label}
                        rules={field.rules}
                        options={field.options || field.radioOptions}
                        errors={errors}
                      />
                    ) : (
                      <ControlledTextInputField
                        name={field.name}
                        control={control}
                        label={field.label}
                        placeholder={field.placeholder}
                        rules={field.rules}
                        options={field.options}
                        type={field.type}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]?.message}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Box className="button-container">
                  <CustomButton
                    variant="contained"
                    className="cancel-button"
                    type="button"
                    text={"Cancel"}
                    onClick={() => addFamilyMemberModalClose()}
                  />
                  <CustomButton
                    variant="contained"
                    className="success-button"
                    loading={loading}
                    type="button"
                    text={"Submit"}
                    onClick={handleSubmit(onSubmit)}
                  />
                </Box>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
